import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PageHeader, Tabs, TabsContent, TabsList, TabsTrigger } from 'lift-ui';
import NotFoundView from '../../components/NotFoundView';
import { useJobsAccess, useOverview } from './hooks';
import { jobsErrorMessage } from './api';
import { OverviewCards } from './components/OverviewCards';
import { JobsTab } from './components/JobsTab';
import { QueuesTab } from './components/QueuesTab';
import { WorkersTab } from './components/WorkersTab';
import { RecurringTasksTab } from './components/RecurringTasksTab';
import { JobDetailSheet } from './components/JobDetailSheet';
import { SyncRunsTab } from './components/SyncRunsTab';
import { SyncToLiftTab } from './components/SyncToLiftTab';

const GrapevineJobsDashboard = () => {
  const [searchParams] = useSearchParams();
  const initialRun = searchParams.get('run');
  const [tab, setTab] = useState(initialRun ? 'sync-runs' : 'jobs');
  const [openRunId, setOpenRunId] = useState<string | null>(initialRun);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const { data: overview, isLoading, isError, error, refetch, isFetching } = useOverview();
  const features = overview?.features;

  const openRun = (batchId: string) => {
    setOpenRunId(batchId);
    setTab('sync-runs');
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Grapevine Jobs"
        actions={[
          {
            label: isFetching ? 'Refreshing…' : 'Refresh',
            variant: 'outline',
            disabled: isFetching,
            onClick: () => refetch(),
          },
        ]}
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">
            {jobsErrorMessage(error, 'The Grapevine jobs service could not be reached.')}
          </p>
        </div>
      ) : (
        <>
          <OverviewCards overview={overview} isLoading={isLoading} />

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="jobs">Jobs</TabsTrigger>
              <TabsTrigger value="queues">Queues</TabsTrigger>
              {features?.workers && <TabsTrigger value="workers">Workers</TabsTrigger>}
              {features?.recurring_tasks && (
                <TabsTrigger value="recurring">Recurring tasks</TabsTrigger>
              )}
              <TabsTrigger value="sync-to-lift">Sync to LIFT</TabsTrigger>
              <TabsTrigger value="sync-runs">Sync Runs</TabsTrigger>
            </TabsList>

            <TabsContent value="jobs" className="mt-4">
              <JobsTab
                supportedStatuses={features?.supported_statuses ?? ['failed']}
                onSelectJob={setSelectedJobId}
              />
            </TabsContent>

            <TabsContent value="queues" className="mt-4">
              <QueuesTab queuePausingSupported={features?.queue_pausing ?? false} />
            </TabsContent>

            {features?.workers && (
              <TabsContent value="workers" className="mt-4">
                <WorkersTab />
              </TabsContent>
            )}

            {features?.recurring_tasks && (
              <TabsContent value="recurring" className="mt-4">
                <RecurringTasksTab />
              </TabsContent>
            )}

            <TabsContent value="sync-to-lift" className="mt-4">
              <SyncToLiftTab onOpenRun={openRun} />
            </TabsContent>

            <TabsContent value="sync-runs" className="mt-4">
              <SyncRunsTab
                enabled={tab === 'sync-runs'}
                onSelectJob={setSelectedJobId}
                initialRunId={openRunId}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      <JobDetailSheet jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
    </div>
  );
};

/**
 * Direct URL access is gated here as well as in the sidebar, so /grapevine-jobs
 * is not reachable by typing it. The gateway is still the real check.
 */
const GrapevineJobs = () => {
  const { canView } = useJobsAccess();
  // Retries only delay the message here: a 403 from the gateway and a 503 from
  // a Rails instance with no jobs server registered both stay failures, and the
  // Refresh button already covers a transient blip.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: false }, mutations: { retry: false } },
      })
  );

  if (!canView) return <NotFoundView />;

  return (
    <QueryClientProvider client={queryClient}>
      <GrapevineJobsDashboard />
    </QueryClientProvider>
  );
};

export default GrapevineJobs;
